/**
 * Lucide icon-class → friendly label resolution.
 *
 * Lucide ships icons as `<svg class="lucide lucide-<name>">…</svg>`. Many
 * design systems (radix/shadcn, custom toolbars) wrap one of these in a
 * `<button>` with no `aria-label`, leaving the accessible name empty. The
 * substrate augmenter extracts the lucide class name as a hint; this module
 * maps it to a human verb the agent can reason over.
 *
 * The hint string flows in as either `lucide:<name>` (preferred — preserves
 * provenance for downstream filters) or the bare `<name>`. Unknown names fall
 * back to title-cased English so even un-mapped icons get a usable label
 * rather than `(icon)`.
 *
 * @module
 */

/**
 * Curated mapping of lucide icon names to UI verbs / nouns. Grow on demand
 * — only entries observed in real captures should land here. Map keys are
 * the lucide class suffix (after `lucide-`).
 */
export const LUCIDE_NAME_MAP: Record<string, string> = {
  'sliders-horizontal': 'Filter',
  'sliders-vertical': 'Filter',
  sliders: 'Filter',
  filter: 'Filter',
  'filter-x': 'Clear filter',
  search: 'Search',
  'search-x': 'Clear search',
  x: 'Close',
  'x-circle': 'Close',
  plus: 'Add',
  'plus-circle': 'Add',
  pencil: 'Edit',
  'pencil-line': 'Edit',
  edit: 'Edit',
  'edit-2': 'Edit',
  'edit-3': 'Edit',
  trash: 'Delete',
  'trash-2': 'Delete',
  download: 'Download',
  upload: 'Upload',
  'chevron-down': 'Expand',
  'chevron-up': 'Collapse',
  'chevron-left': 'Previous',
  'chevron-right': 'Next',
  'chevrons-left': 'First',
  'chevrons-right': 'Last',
  'arrow-left': 'Back',
  'arrow-right': 'Forward',
  'more-horizontal': 'More',
  'more-vertical': 'More',
  menu: 'Menu',
  settings: 'Settings',
  cog: 'Settings',
  bell: 'Notifications',
  user: 'User',
  'log-out': 'Log out',
  'log-in': 'Log in',
  check: 'Confirm',
  'check-circle': 'Confirm',
  copy: 'Copy',
  'external-link': 'Open in new tab',
  info: 'Info',
  'help-circle': 'Help',
  'circle-help': 'Help',
  eye: 'Show',
  'eye-off': 'Hide',
  refresh: 'Refresh',
  'refresh-cw': 'Refresh',
  'rotate-ccw': 'Refresh',
  star: 'Favorite',
  archive: 'Archive',
  'archive-restore': 'Unarchive',
  share: 'Share',
  'share-2': 'Share',
  printer: 'Print',
};

/**
 * Resolve a lucide CSS class (`lucide-sliders-horizontal`) — or the bare icon
 * name (`sliders-horizontal`) — to a friendly UI label. Falls back to
 * title-cased English when the icon isn't in the curated map.
 */
export function resolveLucideName(raw: string): string {
  const key = raw.replace(/^lucide-/, '').toLowerCase();
  const mapped = LUCIDE_NAME_MAP[key];
  if (mapped) return mapped;
  return key
    .split('-')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}
